import React from 'react';
import Image from 'next/image';
import type { LanguageType } from '@/lib/types';

interface ServiceShowcaseCardsProps {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description: string;
  /** Drives the LTR/RTL padding swap when `paddingMode === 'directional'`. */
  language: LanguageType;
  /** `<Image>` intrinsic width hint (Next.js layout-shift / optimizer input). */
  imageWidth: number;
  /** `<Image>` intrinsic height hint. */
  imageHeight: number;
  /**
   * `<Image sizes>` hint — differs between the desktop grid (~33vw) and the
   * mobile swiper (100vw), so the caller decides.
   */
  imageSizes: string;
  /** Image aspect ratio as a CSS value (e.g. `'16 / 9'` desktop, `'3 / 2'` mobile). */
  imageAspectRatio: string;
  /**
   * Responsive class for the outer column gap. Base gap is hardcoded
   * `gap-[1.25rem]` (identical in both variants); this prop carries the
   * breakpoint-scoped bump. Desktop uses `'lg:gap-[1.5rem]'`, mobile uses
   * `'tb:gap-[1.5rem]'`. Passed as a literal so Tailwind picks it up.
   */
  wrapperGapClass: string;
  /**
   * Optional responsive class for the text-block gap. Base is `gap-[1rem]`.
   * Desktop passes `'lg:gap-[1.25rem]'`; mobile omits this prop (no bump).
   */
  textGapClass?: string;
  /**
   * `'directional'` — language-aware `pl`/`pr` swap (desktop behavior, mirrors
   * RTL). `'symmetric'` — `px-[0.5rem]` regardless of language (mobile behavior).
   */
  paddingMode: 'directional' | 'symmetric';
  /** Optional `max-width` (e.g. `'43rem'`) applied to the description paragraph. */
  paragraphMaxWidth?: string;
}

/**
 * Single card for LPServicesSection — used in both the desktop grid and the
 * mobile swiper. Extracted so the section file stays focused on layout
 * orchestration (grid vs swiper) rather than per-card markup.
 *
 * The `device-image-card` className on the image is intentional: the section
 * queries it with `document.querySelectorAll('.device-image-card')` to wire
 * the GSAP parallax animation. Do not rename without updating that selector.
 *
 * Note on the image wrapper edge: this uses `outline-*` (paints outside the
 * box, doesn't get clipped by `overflow-hidden`), unifying what previously
 * drifted between the desktop and mobile variants (the mobile one used
 * `border-*` — visually similar but layout-distinct).
 */
const ServiceShowcaseCards: React.FC<ServiceShowcaseCardsProps> = ({
  imageUrl,
  imageAlt,
  title,
  description,
  language,
  imageWidth,
  imageHeight,
  imageSizes,
  imageAspectRatio,
  wrapperGapClass,
  textGapClass,
  paddingMode,
  paragraphMaxWidth,
}) => {
  // Padding class set, mirroring the two variants exactly.
  const paddingClass =
    paddingMode === 'symmetric'
      ? 'px-[0.5rem]'
      : language === 'english'
        ? 'pl-[0.5rem] pr-[0.75rem] lg:pl-[0.75rem] lg:pr-[2rem]'
        : 'pr-[0.5rem] pl-[0.75rem] lg:pr-[0.75rem] lg:pl-[2rem]';

  return (
    <div className="relative w-full">
      <div className={`flex flex-col gap-[1.25rem] ${wrapperGapClass}`}>
        <div className="
          border-radius-main
          outline-secondary outline-stroke-soft-200
          overflow-hidden
          ">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={imageWidth}
            height={imageHeight}
            sizes={imageSizes}
            loading="lazy"
            style={{
              scale: 1.1,
              width: '100%',
              height: 'auto',
              aspectRatio: imageAspectRatio,
              objectFit: 'cover',
            }}
            className="device-image-card"
          />
        </div>
        <div className={`flex flex-col gap-[1rem] ${textGapClass ?? ''} ${paddingClass}`}>
          <div className="flex flex-row lg:flex-col justify-between lg:items-start lg:gap-[1.25rem]">
            {/* <div className="w-fit order-2 lg:order-1">
              <DevicePartnerBadge partner="hitek" size="desktop" />
            </div> */}

            <h3 className="
              order-1 lg:order-2
              text-text-primary
              text-28--neue-june
            ">
              {title}
            </h3>
          </div>

          <p
            style={paragraphMaxWidth ? { maxWidth: paragraphMaxWidth } : undefined}
            className="
              text-text-quaternary
              text-18--alexandria
              "
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceShowcaseCards;
